import React, { useState } from "react";
import { toast } from "sonner";
import { PartyPopper, Power, X } from "lucide-react";
import { setAvailability, ackListingCelebration } from "@lawyer/services/lawyerApi";

// One-time "You're live" banner — shown the first time the portal opens after listing.
export default function ListedBanner({ available, onDone }) {
  const [busy, setBusy] = useState(false);

  const finish = async (goOnline) => {
    setBusy(true);
    try {
      if (goOnline && !available) {
        await setAvailability({ available: true });
        toast.success("You're online — instant bookings can reach you now");
      }
      await ackListingCelebration();
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong");
    }
    setBusy(false);
  };

  return (
    <section data-testid="listed-banner"
      className="relative mb-6 overflow-hidden rounded-2xl bg-[#0A2540] text-white p-5 sm:p-6 shadow-lg">
      <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#D4AF37]/25 blur-2xl" />
      <div className="pointer-events-none absolute -left-10 -bottom-20 h-48 w-48 rounded-full bg-emerald-400/15 blur-2xl" />
      <button type="button" onClick={() => finish(false)} disabled={busy} aria-label="Dismiss"
        data-testid="listed-banner-dismiss"
        className="absolute right-3 top-3 rounded-full p-1.5 text-white/60 hover:bg-white/10 hover:text-white transition-colors">
        <X size={16} />
      </button>
      <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="h-12 w-12 shrink-0 grid place-items-center rounded-xl bg-[#D4AF37] text-[#0A2540]">
          <PartyPopper size={22} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest font-bold text-[#D4AF37]">Profile approved</p>
          <h2 className="text-lg sm:text-xl font-extrabold leading-tight" style={{ fontFamily: "Poppins" }}>
            You&rsquo;re live on Advoset
          </h2>
          <p className="mt-1 text-sm text-slate-300">
            Clients can now find and book you. Go online to start receiving instant consultations —
            you can switch off any time from the status card.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button type="button" onClick={() => finish(true)} disabled={busy} data-testid="listed-banner-go-online"
            className="inline-flex items-center gap-2 rounded-xl bg-[#D4AF37] px-4 py-2.5 text-sm font-bold text-[#0A2540] hover:brightness-105 active:scale-[0.98] transition disabled:opacity-60">
            <Power size={14} /> {available ? "Great, got it" : "Go online now"}
          </button>
          {!available && (
            <button type="button" onClick={() => finish(false)} disabled={busy} data-testid="listed-banner-later"
              className="rounded-xl border border-white/20 px-4 py-2.5 text-sm font-bold text-white hover:bg-white/10 transition disabled:opacity-60">
              Later
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
